// Pflege des DeepL-Glossars. Gegenstueck zu `routes/checklist.js` in
// cld-backend (`/checklist/glossary/*`).
//
// Zwei Dinge, die dieser Dienst bewusst tut:
//
//   1. ER UEBERSETZT NICHTS SELBST. Das Backend schickt in `message` ein
//      stabiles Token (`GLOSSARY_...`); die Anzeige entsteht in der View aus
//      der i18n-Datei. Servertext wird nie angezeigt.
//   2. ER RECHNET NICHTS NACH. `published_at` und `has_unpublished_changes`
//      kommen fertig aus jeder Antwort.
//
// Die Berechtigung, die `permission()` meldet, ist eine AUSKUNFT und keine
// Absicherung: durchgesetzt wird sie in der Middleware des Backends.
import * as axios from 'axios';

const BASE = '/checklist/glossary';

// Token-Form des Backends. Alles andere in `message` ist Servertext und wird
// nicht angezeigt.
const TOKEN_PATTERN = /^GLOSSARY_[A-Z_]+$/;

/**
 * Bringt jeden Fehlschlag in eine Form, die die View ohne Kenntnis von axios
 * auswerten kann.
 *
 * Wichtig fuer den Aufrufer: `raw` ist das, was an `api_error` weitergereicht
 * werden darf, wenn `token` leer ist — ein abgelaufenes Token kommt hier als
 * HTTP 200 mit `{ status: 'error', message: 'jwt expired' }` an
 * (`server.js: validateUser`), nicht als 401.
 */
function toGlossaryError(body, raw) {
  const data = body && typeof body === 'object' ? body : {};
  const message = typeof data.message === 'string' ? data.message : '';
  const isToken = TOKEN_PATTERN.test(message);

  return {
    glossaryError: true,
    token: isToken ? message : null,
    field: typeof data.field === 'string' ? data.field : null,
    httpStatus: raw && raw.response ? raw.response.status : null,
    body: data,
    // Fuer die Weitergabe an `api_error`: bei einem HTTP-Fehler der
    // axios-Fehler, bei einer 200er-Fehlerantwort der Rumpf.
    raw: raw || data,
  };
}

function unwrap(promise) {
  return promise
    .then((response) => {
      const data = response ? response.data : null;
      if (!data || data.status !== 'success') {
        return Promise.reject(toGlossaryError(data, null));
      }
      return data;
    })
    .catch((err) => {
      if (err && err.glossaryError) return Promise.reject(err);
      const response = err && err.response ? err.response : {};
      return Promise.reject(toGlossaryError(response.data, err));
    });
}

/**
 * Meldet, ob dieser Nutzer das Glossar pflegen darf.
 *
 * Der Endpunkt antwortet jedem angemeldeten Nutzer mit 200; ein `false` ist
 * also eine Antwort und kein Fehler. Scheitert der Aufruf selbst (kein Netz,
 * abgelaufenes Token), wird abgelehnt — der Aufrufer soll ein solches Ergebnis
 * nicht als dauerhaftes "nein" merken.
 *
 * @returns {Promise<boolean>}
 */
function permission() {
  return unwrap(axios.get(`${BASE}/permission`)).then((data) => data.glossary === true);
}

/**
 * Die gepflegten Begriffspaare plus Veroeffentlichungs-Zustand.
 * @returns {Promise<{entries: Array, published_at: ?string, has_unpublished_changes: boolean}>}
 */
function entries() {
  return unwrap(axios.get(`${BASE}/entries`));
}

function createEntry(de, en) {
  return unwrap(axios.post(`${BASE}/create-entry`, { de: de, en: en }));
}

function updateEntry(uid, de, en) {
  return unwrap(axios.post(`${BASE}/update-entry`, { uid: uid, de: de, en: en }));
}

function deleteEntry(uid) {
  return unwrap(axios.post(`${BASE}/delete-entry`, { uid: uid }));
}

/**
 * Uebertraegt den gespeicherten Stand an DeepL. Beide Woerterbuecher entstehen
 * dort aus derselben Paarliste; eine Richtungsauswahl gibt es nicht.
 */
function publish() {
  return unwrap(axios.post(`${BASE}/publish`, {}));
}

export {
  permission,
  entries,
  createEntry,
  updateEntry,
  deleteEntry,
  publish,
};
